import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  socialInboxList,
  socialInboxMarkRead,
  socialInboxMessages,
  socialInboxReply,
} from "@/lib/social-inbox.functions";

export const Route = createFileRoute("/admin/social/inbox")({
  component: InboxPage,
  head: () => ({
    meta: [
      { title: "Social inbox · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function InboxPage() {
  const qc = useQueryClient();
  const list = useServerFn(socialInboxList);
  const messages = useServerFn(socialInboxMessages);
  const reply = useServerFn(socialInboxReply);
  const markRead = useServerFn(socialInboxMarkRead);

  const [status, setStatus] = useState<"all" | "unread" | "assigned" | "resolved">("all");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replyType, setReplyType] = useState<"SMS" | "Email" | "GMB" | "IG" | "FB">("Email");

  const conv = useQuery({
    queryKey: ["social-inbox", { status, query }],
    queryFn: () => list({ data: { status, query: query || undefined } }),
  });
  const msgQuery = useQuery({
    queryKey: ["social-inbox-msg", active],
    queryFn: () => (active ? messages({ data: { conversation_id: active } }) : []),
    enabled: !!active,
  });

  const data: any = conv.data ?? {};
  const convs: any[] = data.conversations ?? [];

  async function sendReply() {
    if (!active || !replyText.trim()) return;
    try {
      await reply({ data: { conversation_id: active, type: replyType, message: replyText } });
      toast.success("Reply sent");
      setReplyText("");
      await qc.invalidateQueries({ queryKey: ["social-inbox-msg", active] });
    } catch (err: any) { toast.error(err?.message ?? "Reply failed"); }
  }

  async function toggleRead(id: string, unread: boolean) {
    try {
      await markRead({ data: { conversation_id: id, unread } });
      await qc.invalidateQueries({ queryKey: ["social-inbox"] });
    } catch (err: any) { toast.error(err?.message ?? "Mark failed"); }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="mb-2 text-2xl font-bold">Social inbox</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Conversations from your connected HighLevel (GHL) location. Direct Meta / LinkedIn native comment
        APIs require separate OAuth we don't have — connect them through GHL to see them here.
      </p>
      <SocialSubNav />

      {data.notice && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          {data.notice}
        </div>
      )}

      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <div>
          <Label>Filter</Label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="assigned">Assigned to me</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Label>Search</Label>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="name, phone, email…" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[320px_1fr]">
        <aside className="max-h-[70vh] overflow-auto rounded border border-border">
          {conv.isLoading ? <p className="p-3 text-xs">Loading…</p> :
            convs.length === 0 ? <p className="p-3 text-xs text-muted-foreground">No conversations.</p> :
            <ul>
              {convs.map((c: any) => (
                <li key={c.id}>
                  <button
                    onClick={() => setActive(c.id)}
                    className={`w-full border-b border-border p-3 text-left text-xs hover:bg-muted ${active === c.id ? "bg-muted" : ""}`}
                  >
                    <div className="font-semibold">{c.contactName || c.name || c.contactId || c.id}</div>
                    <div className="truncate text-muted-foreground">{c.lastMessageBody || c.snippet || "—"}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {c.type || c.lastMessageType || ""} · {c.unreadCount ? `${c.unreadCount} unread` : "read"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          }
        </aside>

        <section className="flex min-h-[70vh] flex-col rounded border border-border">
          {!active ? (
            <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">Select a conversation.</div>
          ) : (
            <>
              <div className="border-b border-border p-3 text-xs">
                <Button variant="outline" size="sm" onClick={() => toggleRead(active, false)}>Mark read</Button>
                <Button variant="outline" size="sm" className="ml-2" onClick={() => toggleRead(active, true)}>Mark unread</Button>
              </div>
              <div className="flex-1 overflow-auto p-3">
                {msgQuery.isLoading ? <p className="text-xs">Loading…</p> :
                  ((msgQuery.data as any[]) ?? []).length === 0 ? <p className="text-xs text-muted-foreground">No messages.</p> :
                  <ul className="space-y-2">
                    {((msgQuery.data as any[]) ?? []).map((m: any) => (
                      <li key={m.id} className={`rounded p-2 text-xs ${m.direction === "outbound" ? "ml-8 bg-primary/10" : "mr-8 bg-muted"}`}>
                        <div className="text-[10px] text-muted-foreground">{m.type} · {m.dateAdded ? new Date(m.dateAdded).toLocaleString() : ""}</div>
                        <div className="mt-1 whitespace-pre-wrap">{m.body || m.messageBody || m.text || ""}</div>
                      </li>
                    ))}
                  </ul>
                }
              </div>
              <div className="border-t border-border p-3">
                <div className="mb-2 flex gap-2">
                  <select
                    value={replyType}
                    onChange={(e) => setReplyType(e.target.value as any)}
                    className="h-8 rounded border border-input bg-background px-2 text-xs"
                  >
                    <option>Email</option><option>SMS</option><option>FB</option><option>IG</option><option>GMB</option>
                  </select>
                </div>
                <Textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Reply…" />
                <div className="mt-2 flex justify-end">
                  <Button size="sm" onClick={sendReply} disabled={!replyText.trim()}>Send</Button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
